/**
 * Payment Methods API Client
 * Handles all wallet/saved cards related API calls
 */

import { api } from '../services/api'
import type {
  PaymentMethod,
  PaymentMethodsResponse,
  SetupIntentResponse,
  SavePaymentMethodRequest,
  SavePaymentMethodResponse
} from '../types/paymentMethod'

/**
 * Get all saved payment methods for the current user
 */
export async function getPaymentMethods(token: string): Promise<PaymentMethodsResponse> {
  const response = await fetch(`${api.baseURL}/api/payment-methods`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch payment methods' }))
    throw new Error(error.error || 'Failed to fetch payment methods')
  }

  return response.json()
}

/**
 * Create a Setup Intent for adding a new card without charging
 */
export async function createSetupIntent(token: string): Promise<SetupIntentResponse> {
  const response = await fetch(`${api.baseURL}/api/payment-methods/setup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create setup intent' }))
    throw new Error(error.error || 'Failed to create setup intent')
  }

  return response.json()
}

/**
 * Save a payment method after Setup Intent confirmation
 */
export async function savePaymentMethod(
  token: string,
  data: SavePaymentMethodRequest
): Promise<SavePaymentMethodResponse> {
  const response = await fetch(`${api.baseURL}/api/payment-methods`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to save payment method' }))
    throw new Error(error.error || 'Failed to save payment method')
  }

  return response.json()
}

/**
 * Delete a saved payment method
 */
export async function deletePaymentMethod(token: string, paymentMethodId: string): Promise<void> {
  const response = await fetch(`${api.baseURL}/api/payment-methods/${paymentMethodId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete payment method' }))
    throw new Error(error.error || 'Failed to delete payment method')
  }
}

/**
 * Set a payment method as default for one-click purchases
 */
export async function setDefaultPaymentMethod(token: string, paymentMethodId: string): Promise<PaymentMethod> {
  const response = await fetch(`${api.baseURL}/api/payment-methods/${paymentMethodId}/default`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to set default payment method' }))
    throw new Error(error.error || 'Failed to set default payment method')
  }

  const result = await response.json()
  return result.payment_method
}
